import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Paper,
  Tooltip,
  Divider,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Dashboard as KanbanIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  MoreVert as MoreVertIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowForwardIcon,
  TableChart as TableChartIcon,
  Android as BotIcon,
} from '@material-ui/icons';
import { AuthContext } from '../../context/Auth/AuthContext';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { useHistory } from 'react-router-dom';
import api from '../../services/api';
import Title from '../../components/Title';
import usePipelines from '../../hooks/usePipelines';
import QueueModal from '../../components/QueueModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import MainHeader from '../../components/MainHeader';
import MainContainer from '../../components/MainContainer';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import { RiRobot2Line } from 'react-icons/ri';

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: 'scroll',
    overflowX: 'hidden',
    ...theme.scrollbarStyles,
  },
  pipelineCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    cursor: 'pointer',
    maxWidth: '100%',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  cardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  pipelineIcon: {
    marginRight: theme.spacing(1.5),
    fontSize: '1.5rem',
    color: theme.palette.primary.main,
  },
  cardContent: {
    flexGrow: 1,
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    color: '#666',
  },
  statIcon: {
    fontSize: 16,
    marginRight: theme.spacing(0.5),
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  tagChip: {
    height: 24,
    fontSize: '0.75rem',
  },
  cardActions: {
    padding: theme.spacing(2),
    paddingTop: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  accessButton: {
    background: 'transparent',
    color: theme.palette.text.primary,
    fontWeight: '500',
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    padding: '8px 16px',
    minWidth: 'auto',
    textTransform: 'none',
    fontSize: '0.875rem',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  configButton: {
    color: '#115133',
    borderRadius: 6,
    minWidth: 'auto',
    '&:hover': {
      opacity: 0.7,
    },
  },
  menuButton: {
    color: '#666',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(8),
    color: '#666',
  },
  emptyIcon: {
    fontSize: 64,
    color: '#ccc',
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 300,
  },
  menuItemIcon: {
    minWidth: 36,
  },
  pinnedSection: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    fontSize: '1.1rem',
    color: theme.palette.text.primary,
  },
  pinButton: {
    color: '#666',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  pinnedCard: {
    boxShadow: `0 0 0 1px ${theme.palette.divider}`,
  },
  divider: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
}));

// Converte hex para rgba (para shadow com opacidade)
const hexToRgba = (hex, alpha) => {
  if (!hex || hex.length < 7) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Componente de ícone de Pin customizado
const PinIcon = ({ isPinned, ...props }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M16 12V4H17V2H7V4H8V12L6 14V16H11.2V22H12.8V16H18V14L16 12Z"
      fill={isPinned ? '#ff9800' : '#666'}
      transform={isPinned ? 'rotate(45 12 12)' : 'rotate(0 12 12)'}
    />
  </svg>
);

const PipelineList = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { profile } = user;
  const { findAll } = usePipelines();

  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [editingQueueId, setEditingQueueId] = useState(null);

  // Função para carregar CRMs fixados do localStorage
  const getPinnedPipelineIds = () => {
    try {
      const pinned = localStorage.getItem('pinnedPipelines');
      return pinned ? JSON.parse(pinned) : [];
    } catch (error) {
      console.error('Erro ao carregar CRMs fixados:', error);
      return [];
    }
  };

  // Função para salvar CRMs fixados no localStorage
  const savePinnedPipelineIds = (pinnedIds) => {
    try {
      localStorage.setItem('pinnedPipelines', JSON.stringify(pinnedIds));
    } catch (error) {
      console.error('Erro ao salvar CRMs fixados:', error);
    }
  };

  // Função para fixar/desfixar um CRM
  const handleTogglePin = (pipelineId, event) => {
    if (event) {
      event.stopPropagation();
    }

    const pinnedIds = getPinnedPipelineIds();
    const isPinned = pinnedIds.includes(pipelineId);

    let newPinnedIds;
    if (isPinned) {
      // Desfixar
      newPinnedIds = pinnedIds.filter(id => id !== pipelineId);
      toast.success(i18n.t('pipelines.menu.unpin'));
    } else {
      // Fixar
      newPinnedIds = [...pinnedIds, pipelineId];
      toast.success(i18n.t('pipelines.menu.pin'));
    }

    savePinnedPipelineIds(newPinnedIds);

    // Atualizar estado local dos pipelines
    setPipelines(prevPipelines =>
      prevPipelines.map(pipeline =>
        pipeline.id === pipelineId
          ? { ...pipeline, isPinned: !isPinned }
          : pipeline
      )
    );
  };

  const getQueueInfo = async queueId => {
    try {
      const { data } = await api.get(`/queue/${queueId}`);
      return {
        haveIntegration: !!data.integrationId,
        color: data.color || null,
      };
    } catch (error) {
      console.error(`Erro ao buscar dados da fila ${queueId}:`, error);
      return { haveIntegration: false, color: null };
    }
  };

  const fetchPipelines = async () => {
    try {
      setLoading(true);

      // Buscar pipelines do backend
      const pipelinesData = await findAll();

      // Filtrar pipelines baseado no perfil do usuário
      // Admin e Manager veem todos, User comum só vê os que está associado
      const currentUserId = user?.id;
      const isAdminOrManager = profile === 'admin' || profile === 'manager';

      console.log('Filtrando pipelines:', {
        currentUserId,
        profile,
        isAdminOrManager,
        totalPipelines: pipelinesData.length,
      });

      const filteredPipelines = isAdminOrManager
        ? pipelinesData
        : pipelinesData.filter(pipeline => {
            // Verifica se o usuário está na lista de usuários do pipeline
            const hasAccess =
              pipeline.users &&
              Array.isArray(pipeline.users) &&
              pipeline.users.some(
                pipelineUser =>
                  pipelineUser.id === currentUserId ||
                  pipelineUser.userId === currentUserId ||
                  String(pipelineUser.id) === String(currentUserId) ||
                  String(pipelineUser.userId) === String(currentUserId)
              );

            if (!hasAccess) {
              console.log(`Usuário ${currentUserId} não tem acesso ao pipeline ${pipeline.id} (${pipeline.name})`);
            }

            return hasAccess;
          });

      console.log('Pipelines filtrados:', {
        total: filteredPipelines.length,
        pipelines: filteredPipelines.map(p => ({ id: p.id, name: p.name })),
      });

      // Carregar CRMs fixados do localStorage
      const pinnedIds = getPinnedPipelineIds();

      // Transformar dados do backend para o formato esperado (cor da borda vem da pipeline ou da fila)
      const transformedPipelines = await Promise.all(
        filteredPipelines.map(async pipeline => {
          const queueInfo = pipeline.queueId
            ? await getQueueInfo(pipeline.queueId)
            : { haveIntegration: false, color: null };
          return {
            id: pipeline.id,
            name: pipeline.name,
            haveIntegration: queueInfo.haveIntegration,
            description: pipeline.description,
            createdAt: pipeline.createdAt,
            totalTickets: pipeline.openTicketsCount || 0,
            totalUsers: pipeline.users?.length || 0,
            tags:
              pipeline.stages
                ?.sort((a, b) => a.order - b.order)
                .map(stage => ({
                  id: stage.id,
                  name: stage.name,
                  color: stage.color,
                })) || [],
            isActive: pipeline.isActive,
            companyId: pipeline.companyId,
            queueId: pipeline.queueId,
            isPinned: pinnedIds.includes(pipeline.id),
            color:
              pipeline.color ||
              pipeline.queue?.color ||
              queueInfo.color ||
              null,
          };
        })
      );

      // Colocar a pipeline "Sem fila" na primeira posição, se existir
      const semFilaIndex = transformedPipelines.findIndex(
        p => p.name?.toLowerCase() === 'sem fila'
      );
      if (semFilaIndex !== -1 && semFilaIndex > 0) {
        const [semFilaPipeline] = transformedPipelines.splice(semFilaIndex, 1);
        transformedPipelines.unshift(semFilaPipeline);
      } else if (semFilaIndex === 0) {
        // Já está na primeira posição, não precisa fazer nada
      }

      console.log(pipelinesData)
      setPipelines(transformedPipelines);
    } catch (error) {
      console.error('Erro ao buscar pipelines:', error);
      toast.error(i18n.t('pipelines.errors.fetchError'));
      setPipelines([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchPipelines();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, profile]);

  const handleAccessPipeline = pipelineId => {
    history.push(`/crm/${pipelineId}`);
  };

  const handleCreatePipeline = () => {
    setQueueModalOpen(true);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setEditingQueueId(null);
    // Recarregar pipelines após criar uma nova fila
    fetchPipelines();
  };

  const handleConfigurePipeline = pipelineId => {
    history.push(`/crm/${pipelineId}/config`);
  };

  const handleDeletePipeline = pipelineId => {
    setSelectedPipeline(pipelines.find(p => p.id === pipelineId));
    setConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPipeline) {
      toast.error(i18n.t('pipelines.errors.noPipelineSelected'));
      setConfirmModalOpen(false);
      return;
    }

    if (!selectedPipeline.queueId) {
      toast.error(i18n.t('pipelines.errors.queueIdNotFound'));
      setConfirmModalOpen(false);
      setSelectedPipeline(null);
      return;
    }

    try {
      await api.delete(`/queue/${selectedPipeline.queueId}`);

      toast.success(i18n.t('pipelines.errors.deleteSuccess'));
      fetchPipelines(); // Recarregar a lista
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        i18n.t('pipelines.errors.unknownError');

      toast.error(
        i18n.t('pipelines.errors.deleteError', { error: errorMessage })
      );
    }

    setConfirmModalOpen(false);
    setSelectedPipeline(null);
    setAnchorEl(null); // Limpar o menu também
  };

  const handleMenuClick = (event, pipeline) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedPipeline(pipeline);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Não limpar selectedPipeline aqui para manter os dados para o modal de confirmação
  };

  const handleMenuAction = action => {
    if (selectedPipeline) {
      switch (action) {
        case 'edit':
          setEditingQueueId(selectedPipeline.queueId);
          setQueueModalOpen(true);
          break;
        case 'stages':
          handleConfigurePipeline(selectedPipeline.id);
          break;
        case 'pin':
        case 'unpin':
          handleTogglePin(selectedPipeline.id);
          break;
        case 'delete':
          handleDeletePipeline(selectedPipeline.id);
          break;
        default:
          break;
      }
    }
    handleMenuClose();
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  // Função para renderizar um card de pipeline
  const renderPipelineCard = pipeline => (
    <Card
      className={`${classes.pipelineCard} ${pipeline.isPinned ? classes.pinnedCard : ''}`}
      onClick={() => handleAccessPipeline(pipeline.id)}
      style={
        pipeline.color
          ? {
              border: '1px solid transparent',
              boxShadow: `0 4px 24px -2px ${hexToRgba(
                pipeline.color,
                0.22
              )}, 0 2px 8px -2px ${hexToRgba(pipeline.color, 0.12)}`,
            }
          : undefined
      }
    >
      <CardContent className={classes.cardContent}>
        <div className={classes.cardHeader}>
          <div className={classes.cardHeaderLeft}>
            <TableChartIcon className={classes.pipelineIcon} />
            <div>
              <Typography variant='h6' component='h3'>
                {pipeline.name}
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {i18n.t('pipelines.createdAt')}: {formatDate(pipeline.createdAt)}
              </Typography>
              <Chip
                label={
                  pipeline.isActive
                    ? i18n.t('pipelines.status.active')
                    : i18n.t('pipelines.status.inactive')
                }
                size='small'
                style={{
                  backgroundColor: pipeline.isActive ? '#4caf50' : '#f44336',
                  color: 'white',
                  fontSize: '0.7rem',
                  height: 20,
                  marginTop: 4,
                }}
              />
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
            }}
          >
            {pipeline.haveIntegration && (
              <Tooltip title={'Integração ativa'}>
                <div>
                  <RiRobot2Line size={25} />
                </div>
              </Tooltip>
            )}
            {/* Botão de fixar - não mostrar para "Sem fila" */}
            {pipeline.name?.toLowerCase() !==
              i18n.t('pipelines.specialNames.noQueue').toLowerCase() && (
              <Tooltip
                title={
                  pipeline.isPinned
                    ? i18n.t('pipelines.menu.unpin')
                    : i18n.t('pipelines.menu.pin')
                }
              >
                <IconButton
                  className={classes.pinButton}
                  onClick={e => handleTogglePin(pipeline.id, e)}
                  size='small'
                >
                  <PinIcon isPinned={pipeline.isPinned} />
                </IconButton>
              </Tooltip>
            )}
            {(profile === 'admin' || profile === 'manager') &&
              pipeline.name?.toLowerCase() !==
                i18n.t('pipelines.specialNames.noQueue') && (
                <IconButton
                  className={classes.menuButton}
                  onClick={e => handleMenuClick(e, pipeline)}
                  size='small'
                >
                  <MoreVertIcon />
                </IconButton>
              )}
          </div>
        </div>
        <Typography variant='body2' color='textSecondary' paragraph>
          {pipeline.description || i18n.t('pipelines.noDescription')}
        </Typography>

        <div className={classes.statsContainer}>
          <div className={classes.statItem}>
            <AssignmentIcon className={classes.statIcon} />
            <Typography variant='body2'>
              {pipeline.totalTickets || 0} {i18n.t('pipelines.stats.tickets')}
            </Typography>
          </div>
          {profile !== 'user' && (
            <div className={classes.statItem}>
              <PeopleIcon className={classes.statIcon} />
              <Typography variant='body2'>
                {pipeline.totalUsers || 0} {i18n.t('pipelines.stats.users')}
              </Typography>
            </div>
          )}
        </div>

        {pipeline.tags && pipeline.tags.length > 0 && (
          <div className={classes.tagsContainer}>
            {pipeline.tags.slice(0, 3).map(tag => (
              <Chip
                key={tag.id}
                label={tag.name}
                size='small'
                className={classes.tagChip}
                style={{
                  backgroundColor: tag.color,
                  color: 'white',
                }}
              />
            ))}
            {pipeline.tags.length > 3 && (
              <Chip
                label={`+${pipeline.tags.length - 3}`}
                size='small'
                className={classes.tagChip}
                variant='outlined'
              />
            )}
          </div>
        )}
      </CardContent>

      <CardActions className={classes.cardActions}>
        <Button
          variant='outlined'
          className={classes.accessButton}
          onClick={e => {
            e.stopPropagation();
            handleAccessPipeline(pipeline.id);
          }}
          endIcon={<ArrowForwardIcon />}
        >
          {i18n.t('pipelines.access')}
        </Button>
      </CardActions>
    </Card>
  );

  if (loading) {
    return (
      <MainContainer>
        <div className={classes.loadingContainer}>
          <CircularProgress />
          <Typography variant='h6' style={{ marginLeft: 16 }}>
            {i18n.t('pipelines.loading')}
          </Typography>
        </div>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t('pipelines.title')}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant='contained'
            color='primary'
            startIcon={<AddIcon />}
            onClick={handleCreatePipeline}
          >
            {i18n.t('pipelines.createNew')}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant='outlined'>
        {pipelines.length === 0 ? (
          <div className={classes.emptyState}>
            <KanbanIcon className={classes.emptyIcon} />
            <Title>{i18n.t('pipelines.empty.title')}</Title>
            <Typography variant='body2'>
              {i18n.t('pipelines.empty.description')}
            </Typography>
          </div>
        ) : (
          <>
            {/* Seção de CRMs Fixados */}
            {pipelines.filter(p => p.isPinned).length > 0 && (
              <div className={classes.pinnedSection}>
                <Typography variant='h6' className={classes.sectionTitle}>
                  {i18n.t('pipelines.pinned.title')}
                </Typography>
                <Grid container spacing={3}>
                  {pipelines
                    .filter(p => p.isPinned)
                    .map(pipeline => (
                      <Grid item xs={12} sm={6} md={4} key={pipeline.id}>
                        {renderPipelineCard(pipeline)}
                      </Grid>
                    ))}
                </Grid>
                {/* Divider entre seções */}
                {pipelines.filter(p => !p.isPinned).length > 0 && (
                  <Divider className={classes.divider} />
                )}
              </div>
            )}

            {/* Seção de CRMs Não Fixados */}
            {pipelines.filter(p => !p.isPinned).length > 0 && (
              <Grid container spacing={3}>
                {pipelines
                  .filter(p => !p.isPinned)
                  .map(pipeline => (
                    <Grid item xs={12} sm={6} md={4} key={pipeline.id}>
                      {renderPipelineCard(pipeline)}
                    </Grid>
                  ))}
              </Grid>
            )}
          </>
        )}
      </Paper>

      {/* Menu de ações */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleMenuAction('edit')}>
          <ListItemIcon className={classes.menuItemIcon}>
            <EditIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText primary={i18n.t('pipelines.menu.edit')} />
        </MenuItem>

        <MenuItem onClick={() => handleMenuAction('stages')}>
          <ListItemIcon className={classes.menuItemIcon}>
            <SettingsIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText primary={i18n.t('pipelines.menu.stages')} />
        </MenuItem>

        {/* Opção de fixar - não mostrar para "Sem fila" */}
        {selectedPipeline?.name?.toLowerCase() !==
          i18n.t('pipelines.specialNames.noQueue').toLowerCase() && (
          <MenuItem
            onClick={() =>
              handleMenuAction(selectedPipeline?.isPinned ? 'unpin' : 'pin')
            }
          >
            <ListItemIcon className={classes.menuItemIcon}>
              <PinIcon
                isPinned={selectedPipeline?.isPinned}
                style={{ width: 20, height: 20 }}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                selectedPipeline?.isPinned
                  ? i18n.t('pipelines.menu.unpin')
                  : i18n.t('pipelines.menu.pin')
              }
            />
          </MenuItem>
        )}

        <MenuItem onClick={() => handleMenuAction('delete')}>
          <ListItemIcon className={classes.menuItemIcon}>
            <DeleteIcon fontSize='small' style={{ color: '#f44336' }} />
          </ListItemIcon>
          <ListItemText primary={i18n.t('pipelines.menu.delete')} />
        </MenuItem>
      </Menu>

      {/* Modal de criação/edição de fila */}
      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={editingQueueId}
      />

      {/* Modal de confirmação de exclusão */}
      <ConfirmationModal
        title={i18n.t('pipelines.confirmation.deleteTitle', {
          name: selectedPipeline?.name,
        })}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleConfirmDelete}
      >
        {i18n.t('pipelines.confirmation.deleteMessage')}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default PipelineList;
